import Phaser from 'phaser'
import {
  Grid,
  SpinTiming,
  ALL_SYMBOLS,
  type SymbolKey,
} from '@/config/SlotConfig'
import { SlotSymbol } from './Symbol'

/**
 * 单列 Reel — 通用 ROWS 可见 + 1 顶 buffer + 1 底 buffer
 *
 * 内部 symbol 实例数 = Grid.ROWS + 2，按 y 排序：
 *   sorted[0]                        → 顶部 buffer
 *   sorted[1..Grid.ROWS]             → 可见行（行号 0..ROWS-1）
 *   sorted[Grid.ROWS + 1]            → 底部 buffer
 *
 * 三段式动画：
 * 1. 加速 (ACCEL_MS, easeIn)
 * 2. 匀速 (直到 stop 信号 + CONSTANT_MIN_MS)
 * 3. 减速对齐 (DECEL_MS, easeOut，精确停在 finalSymbols)
 */
export class Reel {
  private readonly scene: Phaser.Scene
  /** 这一列的中心 x 坐标(设计分辨率) */
  private readonly centerX: number
  /** 可见区 3 个格子的中心 y 坐标 */
  private readonly visibleCenterY: number

  /** Grid.ROWS + 2 个 SlotSymbol 实例（顶 buffer + 可见行 + 底 buffer） */
  private symbols: SlotSymbol[] = []

  /** 当前 scroll 累计偏移 px(单调递增,模 CELL_HEIGHT 计算 symbol 实际位置) */
  private scrollOffset = 0

  /** 当前是否在转 */
  private spinning = false

  /** 当前速度 px/s */
  private speed = 0

  /** stop 信号 + 目标 symbols(从上到下 3 个) */
  private stopRequested = false
  private targetSymbols: SymbolKey[] = []
  private constantStartedAt = 0

  /** 减速 tween, 用于停轮 */
  private decelTween: Phaser.Tweens.Tween | null = null

  /** stop 完成回调 */
  private onStopComplete: (() => void) | null = null

  constructor(scene: Phaser.Scene, colIndex: number) {
    this.scene = scene
    this.centerX = Grid.ORIGIN_X + colIndex * Grid.CELL_WIDTH + Grid.CELL_WIDTH / 2
    this.visibleCenterY = Grid.ORIGIN_Y + (Grid.ROWS * Grid.CELL_HEIGHT) / 2

    this.createInitialSymbols()
  }

  /** 初始化 ROWS+2 个 symbols（顶 buffer + 可见 + 底 buffer），随机起始 */
  private createInitialSymbols(): void {
    const total = Grid.ROWS + 2
    // 中心 i = (total-1)/2 落在可见区中心；y 偏移 (i - (total-1)/2) * CELL_HEIGHT
    const centerIdx = (total - 1) / 2
    for (let i = 0; i < total; i++) {
      const y = this.visibleCenterY + (i - centerIdx) * Grid.CELL_HEIGHT
      const key = randomSymbol()
      const sym = new SlotSymbol(this.scene, this.centerX, y, key)
      this.symbols.push(sym)
    }
  }

  /** 启动转动 */
  startSpin(): void {
    if (this.spinning) return
    this.spinning = true
    this.stopRequested = false
    this.speed = 0
    this.decelTween?.stop()
    this.decelTween = null

    // 加速到 maxSpeed
    this.scene.tweens.add({
      targets: this,
      speed: SpinTiming.MAX_SCROLL_SPEED,
      duration: SpinTiming.ACCEL_MS,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        this.constantStartedAt = this.scene.time.now
      },
    })
  }

  /**
   * 请求停轮, 落在指定 Grid.ROWS 个 symbols（从上到下）
   * - 至少经过 CONSTANT_MIN_MS 的匀速
   * - 然后开 DECEL_MS 减速 tween 精确停在目标
   */
  requestStop(targets: SymbolKey[], onComplete: () => void): void {
    if (targets.length !== Grid.ROWS) {
      throw new Error(`Reel needs ${Grid.ROWS} target symbols, got ${targets.length}`)
    }
    this.targetSymbols = [...targets]
    this.stopRequested = true
    this.onStopComplete = onComplete
  }

  /** 每帧由 Scene update 调用 */
  update(_time: number, deltaMs: number): void {
    if (!this.spinning) return

    // 减速阶段已经接管位移, 不再手动累加
    if (this.decelTween) return

    // 累加滚动
    const dt = deltaMs / 1000
    this.scrollOffset += this.speed * dt
    this.applyScrollOffset()

    // 匀速期到了, 触发减速
    if (
      this.stopRequested &&
      this.constantStartedAt > 0 &&
      this.scene.time.now - this.constantStartedAt >= SpinTiming.CONSTANT_MIN_MS &&
      this.speed >= SpinTiming.MAX_SCROLL_SPEED * 0.99
    ) {
      this.startDeceleration()
    }
  }

  /**
   * 计算最近一个能"对齐到目标"的减速终点 scrollOffset, 然后 tween 过去
   * 减速完成时, 5 个 symbols 的纹理被设为 [buffer_top, target0, target1, target2, buffer_bottom]
   */
  private startDeceleration(): void {
    // 让 scrollOffset 落在 CELL_HEIGHT 的整数倍 + 一段额外的"再多滚 N 格"距离
    // 这样有"减速距离感", 不会一减速就停
    const extraCells = 6
    const currentMod = this.scrollOffset % Grid.CELL_HEIGHT
    const remainder = (Grid.CELL_HEIGHT - currentMod) % Grid.CELL_HEIGHT
    const targetScroll = this.scrollOffset + remainder + extraCells * Grid.CELL_HEIGHT

    // 减速期间, 也要持续把出界 symbol 重置回顶部
    // 这里我们用 onUpdate 来更新位置和回收
    this.decelTween = this.scene.tweens.add({
      targets: this,
      scrollOffset: targetScroll,
      duration: SpinTiming.DECEL_MS,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        this.applyScrollOffset()
      },
      onComplete: () => {
        // 减速完: 强制把 5 个 symbol 的内容设置成 [buffer, t0, t1, t2, buffer]
        // 并把它们摆回标准位置
        this.snapToTargets()
        this.spinning = false
        this.speed = 0
        this.decelTween = null
        const cb = this.onStopComplete
        this.onStopComplete = null
        cb?.()
      },
    })
  }

  /**
   * 根据 scrollOffset, 把 ROWS+2 个 symbol 摆放到正确位置
   * 出界(下方)的 symbol 移到上方并换新内容
   */
  private applyScrollOffset(): void {
    const totalCells = Grid.ROWS + 2
    const totalH = totalCells * Grid.CELL_HEIGHT
    const centerIdx = (totalCells - 1) / 2
    const baseTopY = this.visibleCenterY - centerIdx * Grid.CELL_HEIGHT

    for (let i = 0; i < this.symbols.length; i++) {
      const baseY = baseTopY + i * Grid.CELL_HEIGHT
      // 滚动后的位置 = baseY + scrollOffset, 但要 wrap 到 [baseTopY, baseTopY + totalH)
      let y = baseY + this.scrollOffset
      // wrap
      const offsetFromTop = ((y - baseTopY) % totalH + totalH) % totalH
      y = baseTopY + offsetFromTop

      // 检测是否刚刚 wrap 过来(从底回到顶), 换新 symbol
      const sym = this.symbols[i]!
      const prevY = sym.y
      sym.y = y

      // 简易 wrap 检测: 如果新 y 比旧 y 小很多(跳了一大步), 说明 wrap 了
      if (prevY - y > Grid.CELL_HEIGHT * 2) {
        sym.setSymbol(randomSymbol())
      }
    }
  }

  /**
   * 减速完成时调用: 强制把可见 ROWS 行换成 targetSymbols, 上下 buffer 随机
   * ROWS+2 个位置(按 y 从小到大): buffer_top, t[0..ROWS-1], buffer_bottom
   */
  private snapToTargets(): void {
    const total = Grid.ROWS + 2
    const sorted = [...this.symbols].sort((a, b) => a.y - b.y)
    const centerIdx = (total - 1) / 2
    const baseTopY = this.visibleCenterY - centerIdx * Grid.CELL_HEIGHT
    sorted.forEach((sym, i) => {
      sym.y = baseTopY + i * Grid.CELL_HEIGHT
      sym.setAlpha(1)
    })
    // sorted[0] 上 buffer，sorted[ROWS+1] 下 buffer
    sorted[0]!.setSymbol(randomSymbol())
    sorted[total - 1]!.setSymbol(randomSymbol())
    for (let r = 0; r < Grid.ROWS; r++) {
      sorted[1 + r]!.setSymbol(this.targetSymbols[r]!)
    }
    this.scrollOffset = 0
  }

  /** 当前可见的 ROWS 个 symbols（从上到下） */
  getVisibleSymbols(): SymbolKey[] {
    const sorted = [...this.symbols].sort((a, b) => a.y - b.y)
    const out: SymbolKey[] = []
    for (let r = 0; r < Grid.ROWS; r++) out.push(sorted[1 + r]!.getSymbol())
    return out
  }

  isSpinning(): boolean {
    return this.spinning
  }

  /** 返回本列持有的全部 SlotSymbol 实例（含上下 buffer） */
  getAllSymbols(): readonly SlotSymbol[] {
    return this.symbols
  }

  /** 当前可见的 ROWS 个 SlotSymbol 实例（顶到底，rowIdx 0..ROWS-1）— 中奖动画用 */
  getVisibleSymbolObjects(): SlotSymbol[] {
    const sorted = [...this.symbols].sort((a, b) => a.y - b.y)
    return sorted.slice(1, 1 + Grid.ROWS)
  }
}

function randomSymbol(): SymbolKey {
  return ALL_SYMBOLS[Math.floor(Math.random() * ALL_SYMBOLS.length)]!
}
